import { gptSlotResponseStore } from "@/store/gptSlotResponseStore";
import { gptStore } from "@/store/gptStore";
import { headerBiddingStore } from "@/store/headerBiddingStore";
import {
	isAuctionController,
	isIntervalSecondsPerBreakpoint,
} from "@/ts/guards";
import type { GPTRefreshModule, GPTRefreshModuleConfig } from "@/ts/module";
import type {
	DisplaySlotConfig,
	Exactly,
	SlotRefreshContext,
	SlotTargeting,
} from "@/ts/types";
import { markTiming } from "@/utils/commercialDebug";
import { getWidth } from "@/utils/dom";
import { createIntersectionObserver } from "@/utils/intersectionObserver";
import { getModules } from "@/utils/modules";
import { pipe } from "@/utils/pipe";
import {
	getElementByIdPrefix,
	getSlotElementById,
	getSlotsFromGoogleTag,
	hasEligibleSizeForRefresh,
	refreshSlot,
} from "@/utils/slots";

const eligibleSlotsForRefresh = new Set<string>();
let slotObserverRegistry = new Map<Element, IntersectionObserver>();

export class GPTRefresh implements Exactly<GPTRefreshModule, GPTRefresh> {
	private config: GPTRefreshModuleConfig;
	name = "GPTRefresh" as const;

	constructor(config: GPTRefreshModuleConfig) {
		this.config = config;
	}

	async init(): Promise<void> {
		document.addEventListener("visibilitychange", () => {
			try {
				if (document.hidden) {
					slotObserverRegistry.forEach((observer, _adSlotElement) => {
						// Disconnect observers when the tab becomes inactive
						observer.disconnect();
					});
				} else {
					slotObserverRegistry.forEach((observer, adSlotElement) => {
						// Reconnect observers when the tab becomes active again
						observer.observe(adSlotElement);
					});
				}
			} catch (error: unknown) {
				if (error instanceof Error) {
					console.warn(`Error in visibilitychange: ${error.message}`);
				}
				console.warn(`An unknown error occurred: ${error}`);
			}
		});
	}

	async run(
		commandId: number,
		slotConfigs: DisplaySlotConfig[],
	): Promise<void> {
		const {
			gptRefreshWithAuction,
			intervalSecondsPerBreakpoint,
			cssSelectorExclusionsList,
			advertiserIdExclusionsList,
			campaignIdExclusionsList,
			ivoExclusionsList,
			pageSubtypeExclusionsList,
		} = this.config;
		const breakpoint = getWidth();

		const intervalSeconds = isIntervalSecondsPerBreakpoint(
			intervalSecondsPerBreakpoint,
		)
			? intervalSecondsPerBreakpoint[breakpoint]
			: 16;

		const definedSlots = await getSlotsFromGoogleTag();

		if (definedSlots.length === 0) {
			console.warn("No defined GPT slots found.");
			return;
		}

		const configuredSlots = definedSlots.filter((slot) => {
			const slotElementId = slot.getSlotElementId();
			return slotConfigs.some((config) => config.slotId === slotElementId);
		});

		const { pageMisc } = headerBiddingStore
			.getState()
			.selectors.getAuctionData();
		const { pageType } = pageMisc;
		const pageSubtype = pageType.split(",")[1].trim() ?? "";

		for (const slot of configuredSlots) {
			const slotId = slot.getSlotElementId();

			const shouldRefresh =
				hasEligibleSizeForRefresh(slot) &&
				!eligibleSlotsForRefresh.has(slotId) &&
				!cssSelectorExclusionsList.includes(slotId) &&
				!pageSubtypeExclusionsList.includes(pageSubtype);

			if (shouldRefresh) {
				const slotRefreshContext: SlotRefreshContext = {
					slot,
					slotId,
					commandId,
					interval: null,
					timeInView: 0,
					intervalSeconds,
					gptRefreshWithAuction,
					advertiserIdExclusionsList,
					campaignIdExclusionsList,
					ivoExclusionsList,
				};
				eligibleSlotsForRefresh.add(slotId);
				observeSlotForRefresh(slotRefreshContext);
			}
		}
	}
}

// Functions that handle the slot refresh pipeline

const addElemRefToContext = (
	context: SlotRefreshContext,
): SlotRefreshContext => {
	const element = getSlotElementById(context.slotId);
	if (element instanceof HTMLElement) {
		return { ...context, element };
	}
	return context;
};

const setupObserver = (context: SlotRefreshContext) => {
	if (!context.element) {
		return context;
	}

	// Separate Observers for each slot because the handlers need context.
	const { observer, observerRegistry } = createIntersectionObserver(
		context.element,
		startRefresh(context),
		stopRefresh(context),
	);
	slotObserverRegistry = observerRegistry;
	context.observer = observer;
	observer.observe(context.element);

	return { ...context };
};

const bindVisibilityHandler = (context: SlotRefreshContext) => {
	if (!(context.element && context.observer)) {
		return context;
	}

	return context;
};

const observeSlotForRefresh = pipe(
	addElemRefToContext,
	setupObserver,
	bindVisibilityHandler,
);

// Functions that handle the auction pipeline

const runGptAuction = async (ctx: {
	slotId: string;
	commandId: number;
}): Promise<typeof ctx & { auctionResult?: Record<string, SlotTargeting> }> => {
	const [AuctionModule] = await getModules(["AuctionController"]);
	const { getSlotConfigsByCommandIdAndSlotId } = gptStore.getState().selectors;

	const slotConfigs = getSlotConfigsByCommandIdAndSlotId(
		ctx.commandId,
		ctx.slotId,
	);

	if (isAuctionController(AuctionModule)) {
		const { promise, resolve } = Promise.withResolvers<
			Record<string, SlotTargeting> | undefined
		>();
		AuctionModule.run(slotConfigs, resolve, undefined);
		markTiming(`auction module called for ad refresh: ${ctx.slotId}`);
		const auctionResult = await promise;
		return { ...ctx, auctionResult };
	}

	return { ...ctx, auctionResult: undefined };
};

const auctionPipeline = pipe(runGptAuction, (ctx) => {
	refreshSlot(ctx.slot);
	markTiming(`ad refresh for slotId: ${ctx.slotId}`);
	return ctx;
});

const refreshSlotWithAuction = async (
	commandId: number,
	slotId: string,
	slot: googletag.Slot,
): Promise<void> => {
	await auctionPipeline({ commandId, slotId, slot });
};

// Functions that handle the Intersection Observer

const resetTimer = (context: SlotRefreshContext) => {
	context.timeInView = 0;
};
const handleRefresh = async (context: SlotRefreshContext) => {
	const { slot, slotId, commandId, gptRefreshWithAuction } = context;
	if (gptRefreshWithAuction) {
		await refreshSlotWithAuction(commandId, slotId, slot);
	} else {
		refreshSlot(slot);
		markTiming(`ad refresh for slotId: ${slotId}`);
	}
};
const refreshInterval = async (context: SlotRefreshContext) => {
	if (document.hidden) {
		return;
	}
	context.timeInView += 1;
	if (context.timeInView >= context.intervalSeconds) {
		resetTimer(context);

		const {
			slotId,
			advertiserIdExclusionsList,
			campaignIdExclusionsList,
			ivoExclusionsList,
		} = context;
		const { takeoverDetectionData, slotsResponseInformation } =
			gptSlotResponseStore.getState();
		const { sponsorshipDetected, takeoverSlots } = takeoverDetectionData;
		const { advertiserId = 0, campaignId = 0 } =
			slotsResponseInformation[slotId]?.responseInformation || {};
		const advertiserIdBlocked =
			advertiserId !== null &&
			advertiserIdExclusionsList.includes(advertiserId);
		const campaignIdBlocked =
			campaignId !== null && campaignIdExclusionsList.includes(campaignId);

		const ivoDetected = getElementByIdPrefix("ivoCentreSkinWrapper");

		// Block slot refresh if:
		// 1. A sponsorship takeover is active for this slot, OR
		// 2. The slot's advertiser ID is in the blocklist, OR
		// 3. The slot's campaign ID is in the blocklist
		const shouldBlockRefresh =
			(sponsorshipDetected && takeoverSlots.includes(slotId)) ||
			(ivoDetected && ivoExclusionsList.includes(slotId)) ||
			advertiserIdBlocked ||
			campaignIdBlocked;

		if (shouldBlockRefresh) {
			return;
		}

		await handleRefresh(context);
	}
};
const startRefresh = (context: SlotRefreshContext) => () => {
	if (context.interval !== null) {
		return;
	} // already running
	context.interval = setInterval(() => refreshInterval(context), 1000);
};

const stopRefresh = (context: SlotRefreshContext) => () => {
	if (context.interval !== null) {
		clearInterval(context.interval);
		context.interval = null;
	}
	context.timeInView = 0;
};
